import React, { useEffect, useState } from "react";
import { Modal, ModalOverlay, ModalContent, ModalCloseButton, useToast } from "@chakra-ui/react";
import { Link as RouterLink } from "react-router-dom";
import { api } from "../../lib/api";

const PRESENCE_COLORS = { active: "#22c55e", busy: "#f59e0b", in_meeting: "#a855f7", dnd: "#ef4444", offline: "#94a3b8" };

export default function ChannelInfoModal({ isOpen, onClose, channel, currentMode, onModeChange }) {
  const toast = useToast();
  const [members, setMembers] = useState([]);
  const [canManage, setCanManage] = useState(false);
  const [loading, setLoading] = useState(false);
  const [allUsers, setAllUsers] = useState([]);
  const [search, setSearch] = useState("");

  async function load() {
    if (!channel?._id) return;
    setLoading(true);
    try {
      const [m, u] = await Promise.all([
        api(`/chat/channels/${channel._id}/members`),
        api(`/users/lookup?q=${encodeURIComponent(search)}`).catch(() => ({ users: [] })),
      ]);
      setMembers(m.members || []);
      setCanManage(!!m.canManage);
      setAllUsers(u.users || []);
    } catch (e) { toast({ title: e.message, status: "error" }); }
    finally { setLoading(false); }
  }

  useEffect(() => { if (isOpen) load(); /* eslint-disable-next-line */ }, [isOpen, channel?._id]);
  useEffect(() => {
    if (!isOpen) return;
    const t = setTimeout(() => { load(); }, 200); // debounce search
    return () => clearTimeout(t);
    // eslint-disable-next-line
  }, [search]);

  async function setMode(mode) {
    try {
      await api(`/chat/channels/${channel._id}/prefs`, { method: "PATCH", body: JSON.stringify({ mode }) });
      onModeChange && onModeChange(mode);
    } catch (e) { toast({ title: e.message, status: "error" }); }
  }

  async function add(userId) {
    try {
      const r = await api(`/chat/channels/${channel._id}/members`, { method: "POST", body: JSON.stringify({ userId }) });
      setMembers(r.members || []);
    } catch (e) { toast({ title: e.message, status: "error" }); }
  }
  async function remove(userId) {
    if (!window.confirm("Remove this member?")) return;
    try {
      await api(`/chat/channels/${channel._id}/members/${userId}`, { method: "DELETE" });
      setMembers((prev) => prev.filter((m) => String(m._id) !== String(userId)));
    } catch (e) { toast({ title: e.message, status: "error" }); }
  }

  const memberIds = new Set(members.map((m) => String(m._id)));
  const candidates = allUsers.filter((u) => !memberIds.has(String(u._id)));

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="lg">
      <ModalOverlay />
      <ModalContent bg="white">
        <ModalCloseButton />
        <div className="p-6 max-h-[80vh] flex flex-col">
          <div className="mb-4">
            <h2 className="text-base font-semibold text-ink-800">
              {channel?.kind === "dm" ? "Direct message" : (channel?.name || channel?.project?.name || "Channel")}
            </h2>
            <p className="text-xs text-ink-500 mt-0.5 capitalize">{(channel?.kind || "").replace("_", " ")}</p>
          </div>

          <div className="bg-ink-50 rounded-md p-3 mb-4">
            <div className="text-[10px] font-semibold uppercase tracking-wider text-ink-500 mb-2">Notifications</div>
            <div className="flex gap-1.5">
              {[
                { mode: "all", label: "All", icon: "🔔" },
                { mode: "mentions", label: "Mentions only", icon: "@" },
                { mode: "muted", label: "Muted", icon: "🔕" },
              ].map((opt) => {
                const active = currentMode === opt.mode;
                return (
                  <button
                    key={opt.mode}
                    onClick={() => setMode(opt.mode)}
                    className={[
                      "flex-1 px-3 py-1.5 rounded text-xs font-medium border transition",
                      active ? "bg-blue-50 border-blue-300 text-blue-700" : "bg-surface border-ink-200 text-ink-600 hover:bg-ink-100",
                    ].join(" ")}
                  >
                    {opt.icon} {opt.label}
                  </button>
                );
              })}
            </div>
          </div>

          <div className="flex-1 overflow-y-auto">
            <div className="text-[10px] font-semibold uppercase tracking-wider text-ink-500 mb-2">Members ({members.length})</div>
            {loading ? <div className="text-xs text-ink-400">Loading…</div> : (
              <>
                <ul className="space-y-1 mb-3">
                  {members.map((m) => (
                    <li key={m._id} className="flex items-center gap-2 p-2 hover:bg-ink-50 rounded">
                      <div className="relative w-7 h-7 rounded-full bg-ink-300 text-white text-[10px] font-semibold flex items-center justify-center">
                        {(m.username || "?").charAt(0).toUpperCase()}
                        {m.presenceStatus && (
                          <span className="absolute -bottom-0.5 -right-0.5 w-2 h-2 rounded-full border border-white" style={{ background: PRESENCE_COLORS[m.presenceStatus] }} />
                        )}
                      </div>
                      <RouterLink to={`/users/${m._id}`} className="flex-1 min-w-0 hover:underline">
                        <div className="text-sm font-medium text-ink-800 truncate">{m.username}</div>
                        <div className="text-[10px] text-ink-500 truncate">{m.designation || m.email}</div>
                      </RouterLink>
                      {canManage && (
                        <button onClick={() => remove(m._id)} className="text-xs text-ink-400 hover:text-red-500 px-2" title="Remove">×</button>
                      )}
                    </li>
                  ))}
                </ul>

                {canManage && (
                  <div className="border-t border-ink-100 pt-3">
                    <div className="text-[10px] font-semibold uppercase tracking-wider text-ink-500 mb-2">Add member</div>
                    <input
                      value={search}
                      onChange={(e) => setSearch(e.target.value)}
                      placeholder="Search users..."
                      className="w-full text-sm border border-ink-200 rounded px-3 py-1.5 mb-2 outline-none focus:border-blue-300"
                    />
                    <ul className="space-y-1 max-h-48 overflow-y-auto">
                      {candidates.length === 0 ? (
                        <li className="text-xs text-ink-400 italic px-2 py-1">{search ? "No matches" : "Type to search"}</li>
                      ) : candidates.slice(0, 8).map((u) => (
                        <li key={u._id} className="flex items-center gap-2 p-2 hover:bg-ink-50 rounded">
                          <div className="w-6 h-6 rounded-full bg-ink-300 text-white text-[10px] font-semibold flex items-center justify-center">
                            {(u.username || "?").charAt(0).toUpperCase()}
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="text-xs font-medium text-ink-800 truncate">{u.username}</div>
                            <div className="text-[10px] text-ink-500 truncate">{u.email}</div>
                          </div>
                          <button onClick={() => add(u._id)} className="text-xs px-2 py-1 bg-blue-500 text-white rounded hover:bg-blue-600">Add</button>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </ModalContent>
    </Modal>
  );
}
